import {Auth} from '../../modules/auth.js';
import {isLargeScreenAndUp, isMediumScreenOnly, isSmallScreenOnly} from '../../modules/breakpoint.js';
import {Flip} from '../../modules/flip.js';
import 'smoothscroll';
import {getHttpClient} from '../../modules/httpClient.js';
import {Notification} from '../../modules/notification.js';
import {bar} from '../../components/scheduleBar/scheduleBar.js';

class LearningPathContent {
    /** */
    constructor() {
        this.spinner = document.querySelector('.js-spinner');
        this.contentHolder = document.querySelector('.js-learningPathContent');
        this.errorMessageContainer = document.querySelector('.js-learningPathContentError');
        this.errorMessage = this.errorMessageContainer.querySelector('.js-errorMessage');
    }

    /** */
    showLoadingState() {
        this.spinner.classList.remove('hidden');
        this.contentHolder.classList.add('hidden');
        this.errorMessageContainer.classList.add('hidden');
    }

    /** */
    hideLoadingState() {
        this.spinner.classList.add('hidden');
    }

    /**
     * @param {string} html
     */
    showContent(html) {
        this.contentHolder.innerHTML = html;
        this.contentHolder.classList.remove('hidden');

        requestAnimationFrame(() => {
            this.contentHolder.dispatchEvent(new CustomEvent('lazy:initContent'));

            promiseCardAnimations.then(({initCardAnimations}) => {
                this.contentHolder.querySelectorAll('.card[data-course-id]').forEach((card) => {
                    initCardAnimations(card);
                });
            });
        });
    }

    /** */
    hideContent() {
        this.contentHolder.classList.add('hidden');
    }

    /**
     * @param {Error} error
     */
    showError(error) {
        this.spinner.classList.add('hidden');
        this.errorMessageContainer.classList.remove('hidden');

        if (error.body && error.body.message) {
            this.errorMessage.textContent = `: ${error.message}`;
        }
    }
}

/** @type {string} */
let currentLearningPathSource;
/** @type {LearningPathContent} */
let learningPathContent;
/** @type {string} */
let updateUserLearningPathRoute;

/**
 * @param {string} learningPathSource
 */
async function showLearningPath(learningPathSource) {
    const isEmptySource = !learningPathSource;
    const isSameSource = currentLearningPathSource === learningPathSource;
    if (isSameSource) return;
    if (isEmptySource) {
        learningPathContent.hideContent();
        return;
    }

    learningPathContent.showLoadingState();

    const response = await getHttpClient()
        .get(learningPathSource)
        .catch((error) => learningPathContent.showError(error));

    if (response) {
        learningPathContent.showContent(response);
    }

    learningPathContent.hideLoadingState();
}

/**
 * @param {number} learningPathId
 */
function saveUserSelectedLearningPath(learningPathId) {
    if (Auth.guest()) return;

    getHttpClient()
        .put(updateUserLearningPathRoute, {learning_path_id: learningPathId})
        .catch((error) => {
            const errorMessage = error.message;
            Notification.error(errorMessage);
        });
}

/**
 * @param {string} learningPathUrl
 */
function updateBrowserHistory(learningPathUrl) {
    history.replaceState(null, null, learningPathUrl);
}

/**
 * @param {string|undefined} learningPathId
 * @param {string|undefined} learningPathName
 */
function dispatchGtmEvent(learningPathId, learningPathName) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: 'learning_path_selected',
        learningPathId,
        learningPathName,
    });
}



/**
 * @param {Event} inputEvent
 */
function onLearningPathSelected(inputEvent) {
    const selectedOption = inputEvent.target.selectedOptions[0];
    const learningPathSource = selectedOption.value;
    const learningPathId = selectedOption.dataset.id;
    const learningPathUrl = selectedOption.dataset.url;
    const learningPathName = selectedOption.dataset.name;

    showLearningPath(learningPathSource);
    saveUserSelectedLearningPath(learningPathId || '');
    updateBrowserHistory(learningPathUrl);
    dispatchGtmEvent(learningPathId, learningPathName);

    currentLearningPathSource = learningPathSource;
}

/**
 * @returns {object}
 */
function parseQueryString() {
    const parts = location.search.substring(1).split('&');
    const variables = {};
    for (const part of parts) {
        const keyValue = part.split('=');
        const key = keyValue[0];
        let value = '';
        if (keyValue.length > 1) {
            value = decodeURIComponent(keyValue[1]);
        }

        variables[key] = value;
    }

    return variables;
}

/** */
function smoothScrollToLearningPath() {
    const queryParameter = parseQueryString();
    if (!queryParameter) return;
    if (!queryParameter.learningPath) return;

    const learningPathSection = document.querySelector('#learningPath');
    learningPathSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
    });
}

/**
 * Initializes expansion feature for course cards sections
 */
function initCourseCardsSections() {
    const cardsSections = [...document.querySelectorAll('[data-show-limited-cards]')];
    cardsSections.forEach((section) => setOverlayVisibility(section));
}

/**
 * Sets the visibility of expansion button and overlay if cards exceed 2 rows
 * @param {HTMLElement} cardSection
 */
function setOverlayVisibility(cardSection) {
    const cardsRows = cardSection.querySelector('[data-cards-list]');
    const totalCards = cardsRows.querySelectorAll('[data-card]');
    const toggleExpansionButton = cardSection.querySelector('[data-expandButton]');

    if (isLargeScreenAndUp() && totalCards.length > 8) {
        addExpandableOption(cardsRows, toggleExpansionButton);
    }
    if (isMediumScreenOnly() && totalCards.length > 6) {
        addExpandableOption(cardsRows, toggleExpansionButton);
    }
    if (isSmallScreenOnly() && totalCards.length > 4) {
        addExpandableOption(cardsRows, toggleExpansionButton);
    }
}

/**
 * Show expand button
 * @param {HTMLElement} cardsRows
 * @param {HTMLElement} toggleExpansionButton
 */
function addExpandableOption(cardsRows, toggleExpansionButton) {
    showExpandButton(cardsRows, toggleExpansionButton);
    toggleExpansionButton.classList.remove('hidden');
    const listFlip = new Flip({
        elementToFlip: cardsRows,
        flipProperty: 'maxHeight',
        differentMeasuredProperty: 'height',
        transitionClass: 'course__cardsSection--transition',
        flipFunction: () => {
            cardsRows.classList.toggle('course__cardsSection--expanded');
        },
    });
    toggleExpansionButton.addEventListener('click', () => onToggleExpansion(cardsRows, toggleExpansionButton, listFlip));
    cardsRows.addEventListener('transitionend', () => {
        cardsRows.classList.remove('course__cardsSection--transition');
        cardsRows.style = {};
    });
}

/**
 * Show expand button and add overlay
 * @param {HTMLElement} cardsRows
 * @param {HTMLElement} toggleExpansionButton
 */
function showExpandButton(cardsRows, toggleExpansionButton) {
    const text = toggleExpansionButton.querySelector('[data-button-text]');
    text.innerText = 'More'; // eslint-disable-line unicorn/prefer-dom-node-text-content
    // add overlay to list
    cardsRows.classList.add('courseCardsList__overlay');
}

/**
 * Show contract button and hide overlay
 * @param {HTMLElement} cardsRows
 * @param {HTMLElement} toggleExpansionButton
 */
function showContractButton(cardsRows, toggleExpansionButton) {
    const text = toggleExpansionButton.querySelector('[data-button-text]');
    text.innerText = 'Show fewer'; // eslint-disable-line unicorn/prefer-dom-node-text-content
    cardsRows.classList.remove('courseCardsList__overlay');
}

/**
 * On mouse click expand or contract section
 * @param {HTMLElement} cardsRows
 * @param {HTMLElement} toggleExpansionButton
 * @param {Flip} listFlip
 */
function onToggleExpansion(cardsRows, toggleExpansionButton, listFlip) {
    listFlip.play();
    toggleExpansionButton.querySelector('svg').classList.toggle('courseCardSection__expandButton--rotated');
    if (cardsRows.classList.contains('courseCardsList__overlay')) {
        showContractButton(cardsRows, toggleExpansionButton);
    } else {
        showExpandButton(cardsRows, toggleExpansionButton);
    }
}

const promiseCardAnimations = new Promise((resolve) => {
    window.addEventListener('load', resolve);
}).then(() => {
    return import('../../components/cards/course.js');
});

/** [no description; please add] */
function initLightActions() {
    const learningPathMenu = document.querySelector('#learningPathName');
    learningPathContent = new LearningPathContent();
    updateUserLearningPathRoute = learningPathMenu.dataset.updateUserPrefRoute;

    learningPathMenu.addEventListener('change', onLearningPathSelected);
    smoothScrollToLearningPath();
    initCourseCardsSections();
    bar.runCountdowns();
}

document.addEventListener('DOMContentLoaded', initLightActions);
